import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { resolveInitialMapView } from '../utils/initialMapView';
import { getUserTimezone } from '../utils/timezone';
import { fetchServerMapView, writeLocalMapView, createDebouncedMapViewSaver } from '../utils/mapViewStorage';

/**
 * GeofencingContext — persists Geofencing page state across navigation AND page refresh.
 * Lives above the router so it never unmounts when switching pages.
 * Key state is backed up to sessionStorage so it survives F5 refresh.
 *
 * Mirrors the LiveTrackingContext pattern: lightweight, client-only, zero server load.
 * Only persists UI state (filters, viewport, layer toggles, selection IDs) — never raw data arrays.
 */
const GeofencingContext = createContext(null);

export const useGeofencing = () => {
  const ctx = useContext(GeofencingContext);
  if (!ctx) throw new Error('useGeofencing must be used within GeofencingProvider');
  return ctx;
};

const STORAGE_KEY = 'gf_state';

const loadPersistedState = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return null;
};

const detectTimezone = () => {
  try {
    return getUserTimezone() || Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch { return null; }
};

export const GeofencingProvider = ({ children }) => {
  const saved = loadPersistedState();

  // Resolve the initial map view once. Priority: sessionStorage → local cache → timezone → Sydney.
  const [initialView] = useState(() => resolveInitialMapView(
    'geofencing',
    saved?.mapCenter ? { center: saved.mapCenter, zoom: saved.mapZoom } : null,
    detectTimezone(),
  ));

  // ── Active tab (zones / places / routes) ──
  const [persistedActiveTab, setPersistedActiveTab] = useState(saved?.activeTab || null);

  // ── Map viewport ──
  const [mapCenter, setMapCenter] = useState(initialView.center);
  const [mapZoom, setMapZoom] = useState(initialView.zoom);

  // ── Mobile List/Map toggle ──
  const [mobileGeoView, setMobileGeoView] = useState(saved?.mobileGeoView || 'list');

  // ── Layer visibility toggles ──
  const [showZonesLayer, setShowZonesLayer] = useState(saved?.showZonesLayer !== undefined ? saved.showZonesLayer : true);
  const [showPlacesLayer, setShowPlacesLayer] = useState(saved?.showPlacesLayer !== undefined ? saved.showPlacesLayer : true);
  const [showRoutesLayer, setShowRoutesLayer] = useState(saved?.showRoutesLayer !== undefined ? saved.showRoutesLayer : true);
  const [showPOI, setShowPOI] = useState(saved?.showPOI || false);

  // ── Sidebar search/grouping ──
  const [searchQuery, setSearchQuery] = useState(saved?.searchQuery || '');
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set(saved?.collapsedGroups || []));

  // ── Selection IDs (persist by id, re-resolve from fresh data on load) ──
  const [selectedZoneId, setSelectedZoneId] = useState(saved?.selectedZoneId || null);
  const [selectedPlaceId, setSelectedPlaceId] = useState(saved?.selectedPlaceId || null);
  const [selectedRouteId, setSelectedRouteId] = useState(saved?.selectedRouteId || null);

  // ── Bulk selection IDs ──
  const [selectedZoneIds, setSelectedZoneIds] = useState(saved?.selectedZoneIds || []);
  const [selectedPlaceIds, setSelectedPlaceIds] = useState(saved?.selectedPlaceIds || []);
  const [selectedRouteIds, setSelectedRouteIds] = useState(saved?.selectedRouteIds || []);

  // ── Async server reconcile: pull last-saved server view and warm the local cache
  // for the NEXT cold load. Never overwrites the current in-session view.
  useEffect(() => {
    if (initialView.source !== 'timezone' && initialView.source !== 'fallback') return;
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    const token = sessionStorage.getItem('impersonate_token') || sessionStorage.getItem('quick_login_token') || localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!apiBase || !token) return;
    let cancelled = false;
    (async () => {
      const server = await fetchServerMapView('geofencing', apiBase, token);
      if (cancelled || !server) return;
      writeLocalMapView('geofencing', server);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Debounced save on map pan/zoom (local + server) ──
  // Skip the first render so the seeded initial view isn't re-saved as a fresh event.
  const saveMapViewRef = useRef(null);
  const isInitialSaveMountRef = useRef(true);
  useEffect(() => {
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    if (!apiBase) return;
    saveMapViewRef.current = createDebouncedMapViewSaver(
      'geofencing',
      apiBase,
      () => sessionStorage.getItem('impersonate_token') || sessionStorage.getItem('quick_login_token') || localStorage.getItem('token') || sessionStorage.getItem('token'),
    );
  }, []);
  useEffect(() => {
    if (isInitialSaveMountRef.current) { isInitialSaveMountRef.current = false; return; }
    if (!saveMapViewRef.current || !mapCenter) return;
    saveMapViewRef.current({ lat: mapCenter.lat, lng: mapCenter.lng, zoom: mapZoom });
  }, [mapCenter, mapZoom]);

  // ── Save to sessionStorage on state changes ──
  useEffect(() => {
    const state = {
      activeTab: persistedActiveTab,
      mapCenter, mapZoom,
      mobileGeoView,
      showZonesLayer, showPlacesLayer, showRoutesLayer, showPOI,
      searchQuery,
      collapsedGroups: [...collapsedGroups],
      selectedZoneId, selectedPlaceId, selectedRouteId,
      selectedZoneIds, selectedPlaceIds, selectedRouteIds,
    };
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* ignore */ }
  }, [persistedActiveTab, mapCenter, mapZoom, mobileGeoView,
      showZonesLayer, showPlacesLayer, showRoutesLayer, showPOI,
      searchQuery, collapsedGroups,
      selectedZoneId, selectedPlaceId, selectedRouteId,
      selectedZoneIds, selectedPlaceIds, selectedRouteIds]);

  // ── Toggle helper for collapsed groups ──
  const toggleGroupCollapsed = useCallback((name) => {
    setCollapsedGroups((previous) => {
      const next = new Set(previous);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  return (
    <GeofencingContext.Provider value={{
      persistedActiveTab, setPersistedActiveTab,
      mapCenter, setMapCenter,
      mapZoom, setMapZoom,
      mobileGeoView, setMobileGeoView,
      showZonesLayer, setShowZonesLayer,
      showPlacesLayer, setShowPlacesLayer,
      showRoutesLayer, setShowRoutesLayer,
      showPOI, setShowPOI,
      searchQuery, setSearchQuery,
      collapsedGroups, setCollapsedGroups, toggleGroupCollapsed,
      selectedZoneId, setSelectedZoneId,
      selectedPlaceId, setSelectedPlaceId,
      selectedRouteId, setSelectedRouteId,
      selectedZoneIds, setSelectedZoneIds,
      selectedPlaceIds, setSelectedPlaceIds,
      selectedRouteIds, setSelectedRouteIds,
    }}>
      {children}
    </GeofencingContext.Provider>
  );
};
